import { Typography, Box, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import assetIcon from "../../../../assets/onprem_assets.svg";
import securityScoreIcon from "../../../../assets/onprem_security_score.svg";
import misconfigIcon from "../../../../assets/onprem_misconfig.svg";
import cveScoreIcon from "../../../../assets/onprem_cv_score.svg";
import "./region.scss";
import { IRegion } from "../../shared/types";
import { ComplianceCellRenderer } from "../../cell_renderers";
import { Metric } from "../../shared";
import Tooltip from "@mui/material/Tooltip";
import lowBug from "../../../../assets/low_bug.svg";
import mdmBug from "../../../../assets/mdm_bug.svg";
import hghBug from "../../../../assets/Hgh_bug.svg";
import criBug from "../../../../assets/cri_bug.svg";

const getCveBugIcon = (score: number, isDarkMode: boolean) => {
  if (!isDarkMode) return cveScoreIcon;
  if (score >= 90) return criBug;
  if (score >= 70) return hghBug;
  if (score >= 40) return mdmBug;
  return lowBug;
};

interface RegionProps {
  data: IRegion & Record<string, any>;
  onClick?: (data: IRegion) => void;
  selectedCompliance?: string;
}

const Region = ({ data, onClick, selectedCompliance = 'CIS_8' }: RegionProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  
  // Get the compliance value based on the selected compliance type
  const getComplianceValue = () => {
    // Debug logs
    console.log('Selected Compliance Field:', selectedCompliance);
    console.log('Data:', data);
    console.log('Raw Compliance Value:', data[selectedCompliance]);
    
    // Access the compliance value directly from the data object
    const rawValue = data[selectedCompliance];
    
    // Handle different value formats
    let numericValue = 0;
    if (typeof rawValue === 'number') {
      numericValue = Math.round(rawValue);
    } else if (typeof rawValue === 'string') {
      // Remove any % sign if present and parse
      numericValue = Math.round(parseFloat(rawValue.replace('%', '')));
    }
    
    // Ensure the value is a valid number between 0 and 100
    numericValue = isNaN(numericValue) ? 0 : Math.min(Math.max(numericValue, 0), 100);
    
    console.log('Final Compliance Value:', numericValue);
    return numericValue;
  };

  const complianceValue = getComplianceValue();

  return (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
    <Grid 
      container
      overflow={"auto"}
      className={`${isDarkMode? "prem-item_dark" :"prem-item"} no-drag clickable-area`}
      alignItems="center"
      key={data.id}
      onClick={() => onClick && onClick(data)}
      style={{ cursor: !data.leaf_node ? 'pointer' : 'default' }}
    >
      <Grid size={{ xs: 2 }} className="prem-item-left">
        <Box className="bg-image-onprem" />
        <Stack alignItems="center" justifyContent="center">
        <Tooltip
  title={data.region_name}
  arrow
  slotProps={{
    tooltip: {
      sx: {
        backgroundColor: "#fff", 
        color: "#000",           
        boxShadow: 1,
        fontSize: "0.775rem",
      },
    },
    arrow: {
      sx: {
        color: "#fff", 
      },
    },
  }}
>
  <Typography
    className="region-title"
    style={{
      textOverflow: "ellipsis",
      marginLeft: "20px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      width: "90px",
    }}
  >
    {data.region_name}
  </Typography>
</Tooltip>

        </Stack>
      </Grid>

     
      
<Box sx={{ display: 'flex', flex: 1,p:1, minWidth: 0, overflowX: 'auto',alignContent: "flex-start", gap: { xs: 1, sm: 1 }, justifyContent: 'flex-start', marginLeft: '50px' }}>
  {[
    { icon: assetIcon, value: data.devices, description: "Asset Count" },
    { icon: securityScoreIcon, value: data.securityscore, description: "Security Score" },
    { icon: misconfigIcon, value: data.vulnerability, description: "Misconfigurations" },
    { icon: getCveBugIcon(data.cve_score, isDarkMode), value: data.cve_score, description: "CVE Score" }
  ].map((stat, index) => (
    <Box key={index} sx={{ minWidth: 0, flex: '0 0 80px', px: { xs: 0.5, sm: 1 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Tooltip
  title={`${stat.description}: ${stat.value ?? 0}`}
  arrow
  slotProps={{
    tooltip: {
      sx: {
        backgroundColor: "#fff", 
        color: "#000",           
        boxShadow: 1,
        fontSize: "0.775rem",
      },
    },
    arrow: {
      sx: {
        color: "#fff", 
      },
    },
  }}
>
  <span>
    <Metric
      index={index}
      icon={stat.icon}
      value={stat.value || 0}
      icon_desc={stat.description}
    />
  </span>
</Tooltip>

    </Box>
  ))}
  {/* Render ComplianceCellRenderer as the last item in the row */}
  <Box sx={{ minWidth: 0, flex: '0 0 100px', px: { xs: 0.5, sm: 1 }, display: 'flex', alignItems: 'center', justifyContent: 'center',marginLeft: '-10px' }}>
    <ComplianceCellRenderer value={complianceValue} />
  </Box>
</Box>
    </Grid>
    </Box>    
  );
};

export default Region;
